import React, { useState } from 'react';
import { Modal, Row, Col, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { post } from 'helpers/api';
import { Form } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ShareLinkEmailInput from './inputs/share_link_email_input';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { isValidEmail } from './helpers/is_valid_email';
import Button from 'shared/button';
import Snackbar from 'shared/snackbar';

export default function ShareLinkByEmailModal({
  additionalInviteProps = {},
  description,
  email,
  fromEmails,
  hideModal,
  leadId,
  propertyId,
  show,
  title
}) {
  const [newInvite, setNewInvite] = useState(getDefaultNewInvite(email, fromEmails));
  const [error, setError] = useState('');
  const [errorDetail, setErrorDetail] = useState('');
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const validEmail = isValidEmail(newInvite.email);
  const updateField = (field, value) => setNewInvite({ ...newInvite, [field]: value });

  const save = (e) => {
    e.preventDefault();
    if (isSendingInvite) {
      return;
    }
    if (!validEmail) return;

    setIsSendingInvite(true);
    setError('');
    setErrorDetail('');

    post(`/docs/document_share_links`, {
      propertyId,
      invite: {
        email: newInvite.email,
        from_user_id: newInvite.from,
        message: newInvite.message,
        lead_id: leadId,
        ...additionalInviteProps
      }
    }).then(() => {
      closeModal();

      setIsSent(true);
    }).catch((error) => {
      setError('We were unable to send the invite, please try again or contact support');
      if (error.response?.status === 422) {
        setErrorDetail(error.response.data?.error);
      }
      setIsSendingInvite(false);
    });
  };

  const closeModal = () => {
    hideModal();
    setError('');
    setErrorDetail('');
    setIsSendingInvite(false);
    setNewInvite(getDefaultNewInvite(email, fromEmails));
  };

  return (
    <React.Fragment>
      <Modal className="share-link-modal document-center" show={show} onHide={() => closeModal()}>
        <Modal.Body className="modal-padding">
          <div className="d-flex justify-content-center mb-5">
            <FontAwesomeIcon className="icon-gradient" icon={faPaperPlane} size="3x"/>
          </div>
          <h3 className="my-5 bold text-center">{title}</h3>
          <p className="spaced-text mb-5 text-center">
            {description}
          </p>
          {error && (
            <Alert bsStyle="danger" className="d-flex align-items-center">
              <FontAwesomeIcon icon={faExclamationCircle} size="2xl"/>
              <div>
                <h4 className="my-0 ml-3">{error}</h4>
                {errorDetail && (
                  <p className="mt-2 mb-0 ml-3">Error Details: {errorDetail}</p>
                )}
              </div>
            </Alert>
          )}

          <Form.Group>
            <Row>
              {!email && <Col sm={7}>
                <ShareLinkEmailInput
                  disabled={isSendingInvite}
                  email={newInvite.email}
                  identifier={newInvite.id}
                  isValidEmail={isValidEmail(newInvite.email)}
                  onChange={(fieldName, newValue) => updateField(fieldName, newValue)}
                />
              </Col>}
              <Col className="mb-4" sm={email ? 12 : 5}>
                <Form.Label className="mb-2">From</Form.Label>
                <Form.Control
                  as="select"
                  disabled={isSendingInvite}
                  onChange={e => updateField('from', e.target.value)}
                >
                  {fromEmails.map(fromEmail => (
                    <option key={fromEmail.id} value={fromEmail.id}>{fromEmail.name}</option>
                  ))}
                </Form.Control>
              </Col>
            </Row>
            <div className="mt-2">
              <Form.Control
                as="textarea"
                disabled={isSendingInvite}
                placeholder="Message"
                onChange={e => updateField('message', e.target.value)}
              />
            </div>

            <div className="mt-4 d-flex justify-content-center">
              <Button.Cancel className="mr-3" disabled={isSendingInvite} outlined onClick={closeModal}>
                Cancel
              </Button.Cancel>
              <Button.Primary disabled={isSendingInvite || !validEmail} onClick={save}>
                {isSendingInvite ? 'Inviting...' : 'Invite'}
              </Button.Primary>
            </div>
          </Form.Group>
        </Modal.Body>
      </Modal>

      <Snackbar
        position="bottom"
        show={isSent}
        text="Email was sent"
        variant="success"
        onClose={() => setIsSent(false)}
      />
    </React.Fragment>
  );
}

const getDefaultNewInvite = (email, fromEmails) => ({ email: email || '', from: fromEmails[0].id, accessLevel: 'low' });

ShareLinkByEmailModal.propTypes = {
  additionalInviteProps: PropTypes.object,
  description: PropTypes.string.isRequired,
  email: PropTypes.string,
  fromEmails: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  hideModal: PropTypes.func.isRequired,
  leadId: PropTypes.number,
  propertyId: PropTypes.number.isRequired,
  show: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
};
